import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"
import { assertPortalLandingValue } from "@productlane/lib/src/portal-landing"
import {
	MAX_GROUP_ORDER_KEYS,
	nextGroupOrder,
} from "@productlane/lib/src/roadmap-custom-order"
import { validateLanguageCode } from "@productlane/lib/src/translations/validate-language"

import type { MutatorTx, RoadmapSettings } from "../schema"
import type { UpdateRoadmapSettingsArgs, UpdateWorkspaceArgs } from "../types"

export const workspaceMutators = {
	update: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: UpdateWorkspaceArgs }) => {
			// Validate language codes if provided
			if (args.defaultLanguage) {
				validateLanguageCode(args.defaultLanguage)
			}
			if (args.enabledLanguages) {
				for (const language of args.enabledLanguages) {
					validateLanguageCode(language)
				}
			}

			assertPortalLandingValue(args.portalLandingSection)

			// Convert selectedTeamIds object to array format
			const selectedTeamIds = args.selectedTeamIds
				? Object.entries(args.selectedTeamIds)
						.map(([key, value]) => (value ? key : undefined))
						.filter((x) => x !== undefined)
				: undefined

			await tx.mutate.workspaces.update({
				id: args.id,
				logoUrl: args.logoUrl,
				name: args.name,
				defaultLinearTeamId: args.defaultTeamId,
				defaultLanguage: args.defaultLanguage,
				enabledLanguages: args.enabledLanguages,
				selectedTeamIds: selectedTeamIds,
				linearSyncTeamIds: args.linearSyncTeamIds,
				hasBeenSetup: args.hasBeenSetup,
				domainPublic: args.domainPublic,
				helpCenterPublic: args.helpCenterPublic,
				changelogPublic: args.changelogPublic,
				timezone: args.timezone,
				officeHours: args.officeHours,
				changelogAutomaticDrafts: args.changelogAutomaticDrafts,
				changelogEmailNotifications: args.changelogEmailNotifications,
				changelogCustomPrompt: args.changelogCustomPrompt,
				changelogLink1Description: args.changelogLink1Description,
				changelogLink1Url: args.changelogLink1Url,
				changelogLink2Description: args.changelogLink2Description,
				changelogLink2Url: args.changelogLink2Url,
				isGoogleAuthRequired: args.isGoogleAuthRequired,
				googleDomain: args.googleDomain,
				isGoogleAnalyticsEnabled: args.isGoogleAnalyticsEnabled,
				googleTagId: args.googleTagId,
				isPosthogEnabled: args.isPosthogEnabled,
				usePortalLogoUrl: args.usePortalLogoUrl,
				portalLogoUrl: args.portalLogoUrl,
				portalSwitcherVisible: args.portalSwitcherVisible,
				portalLandingSection: args.portalLandingSection,
				isDueDateEnabled: args.isDueDateEnabled,
				isRoadmapsEnabled: args.isRoadmapsEnabled,
				weeklyRoadmapEmailEnabled: args.weeklyRoadmapEmailEnabled,
				darkModeAccentColor: args.darkModeAccentColor,
				accentColor: args.accentColor,
				helpCenterTitle: args.helpCenterTitle,
				helpCenterSummary: args.helpCenterSummary,
				helpCenterShowAiActions: args.helpCenterShowAiActions,
				hideBranding: args.hideBranding,
				portalEmbedImageUrl: args.portalEmbedImageUrl,
				darkMode: args.darkMode,
				buttonTextColor: args.buttonTextColor,
				darkModeButtonTextColor: args.darkModeButtonTextColor,
				portalBackgroundColor: args.portalBackgroundColor,
				portalDarkModeBackgroundColor: args.portalDarkModeBackgroundColor,
				subscribeUrl: args.subscribeUrl,
				aiPayPerUseEnabled: args.aiPayPerUseEnabled,
				aiResolutionMonthlyLimit: args.aiResolutionMonthlyLimit,
				slaEnabled: args.slaEnabled,
				slaUseBusinessHours: args.slaUseBusinessHours,
				autoAssignFromLinearOwner: args.autoAssignFromLinearOwner,
				domainCompanyAssignmentEnabled: args.domainCompanyAssignmentEnabled,
				autoSwitchContactToLatestSender: args.autoSwitchContactToLatestSender,
				autoProposeDocsUpdates: args.autoProposeDocsUpdates,
				docsCustomPrompt: args.docsCustomPrompt,
				docsProposalCadence: args.docsProposalCadence,
				docsAutoMergeEnabled: args.docsAutoMergeEnabled,
			})
		},
	),
	updateRoadmapSettings: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: UpdateRoadmapSettingsArgs
			ctx: ZeroJwtPayload
		}) => {
			if (args.workspaceId !== authData.workspaceId) {
				throw new Error("Roadmap settings belong to another workspace")
			}
			if (args.groupOrder && authData.role === "VIEWER") {
				throw new Error("Viewers cannot reorder the roadmap")
			}
			const groupOrder = args.groupOrder
				? nextGroupOrder([], args.groupOrder.slice(0, MAX_GROUP_ORDER_KEYS))
				: undefined

			const [existing] = await tx.query.roadmapSettings
				.where("workspaceId", "=", args.workspaceId)
				.run()

			if (existing) {
				await tx.mutate.roadmapSettings.update({
					workspaceId: args.workspaceId,
					sortOrder: args.sortOrder ?? (existing as RoadmapSettings).sortOrder,
					linearIssueDescription:
						args.linearIssueDescription ??
						(existing as RoadmapSettings).linearIssueDescription,
					googleAuth:
						args.googleAuth ?? (existing as RoadmapSettings).googleAuth,
					googleDomain:
						args.googleDomain ?? (existing as RoadmapSettings).googleDomain,
					groupOrder: groupOrder ?? (existing as RoadmapSettings).groupOrder,
					groupIssuesUnderProject:
						args.groupIssuesUnderProject ??
						(existing as RoadmapSettings).groupIssuesUnderProject,
				})
			} else {
				await tx.mutate.roadmapSettings.insert({
					workspaceId: args.workspaceId,
					sortOrder: args.sortOrder ?? "SCORE_DESC",
					linearIssueDescription: args.linearIssueDescription ?? false,
					googleAuth: args.googleAuth ?? false,
					googleDomain: args.googleDomain ?? null,
					groupOrder: groupOrder ?? [],
					groupIssuesUnderProject: args.groupIssuesUnderProject ?? true,
				})
			}
		},
	),
}
